import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, LoadingSpinner } from '../components/ui';
import { EventMap } from '../components/EventMap';
import { ReportModal } from '../components/moderation/ReportModal';
import { CommentSection } from '../components/CommentSection';
import { useCategory } from '../hooks/useCategories';
import { useAuth } from '../hooks/useAuth';
import { eventService, type Event } from '../services/eventService';
import { db } from '../utils/firebaseConfig';
import { trackEventViewed, trackEventInterested } from '../services/analyticsService';

export function EventDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isInterested, setIsInterested] = useState(false);
  const [interestedLoading, setInterestedLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const { category: eventCategory } = useCategory(event?.category || '');

  useEffect(() => {
    if (id) {
      fetchEvent(id);
    }
  }, [id]);

  // Check authentication status and show prompt if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      setShowAuthPrompt(true);
    }
  }, [authLoading, user]);

  // Check if user is interested in this event
  useEffect(() => {
    if (event && user) {
      setIsInterested(event.interestedUsers?.includes(user.uid) || false);
    }
  }, [event, user]);

  const fetchEvent = async (eventId: string) => {
    try {
      setLoading(true);
      const eventDoc = await getDoc(doc(db, 'events', eventId));

      if (eventDoc.exists()) {
        const eventData = {
          id: eventDoc.id,
          ...eventDoc.data()
        } as Event;
        setEvent(eventData);

        // Track event view
        trackEventViewed({
          event_id: eventId,
          category: eventData.category || '',
          is_free: eventData.isFree ?? true,
          source: 'direct',
        });
      } else {
        setError('Event not found');
      }
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatorClick = () => {
    if (event?.createdBy) {
      navigate(`/users/${event.createdBy}`);
    }
  };

  const handleWebsiteClick = () => {
    if (event?.website) {
      window.open(event.website, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDeleteEvent = async () => {
    if (!id || !event) return;

    try {
      setDeleting(true);
      await eventService.deleteEvent(id);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event. Please try again.');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleToggleInterested = async () => {
    if (!user || !event?.id) return;

    try {
      setInterestedLoading(true);
      const newIsInterested = await eventService.toggleInterested(
        event.id,
        user.uid,
        user.displayName || user.email || 'Anonymous',
        user.photoURL || undefined
      );

      setIsInterested(newIsInterested);

      // Track interest toggle
      const newInterestedCount = newIsInterested
        ? (event.interested || 0) + 1
        : Math.max((event.interested || 0) - 1, 0);

      trackEventInterested({
        event_id: event.id,
        category: event.category || '',
        is_interested: newIsInterested,
        interested_count: newInterestedCount,
      });

      // Update the event's interestedUsers array locally
      setEvent(prev => {
        if (!prev) return prev;
        const interestedUsers = prev.interestedUsers || [];
        return {
          ...prev,
          interestedUsers: newIsInterested
            ? [...interestedUsers, user.uid]
            : interestedUsers.filter(id => id !== user.uid),
          interested: newInterestedCount
        };
      });
    } catch (err) {
      console.error('Error toggling interested:', err);
      alert('Failed to update interest. Please try again.');
    } finally {
      setInterestedLoading(false);
    }
  };

  const handleSignIn = () => {
    // Save the current URL to return after sign in
    const returnUrl = window.location.pathname;
    navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  const handleSignUp = () => {
    // Save the current URL to return after sign up
    const returnUrl = window.location.pathname;
    navigate(`/login?mode=signup&returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600 mt-4">Loading event...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{error || 'Event not found'}</h2>
        <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has been removed.</p>
        <Button onClick={() => navigate('/')}>
          Back to Events
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Event Image */}
      <div className="relative w-full h-48 sm:h-56 md:h-80 mb-6 md:mb-8 rounded-2xl overflow-hidden">
        {event.image || event.linkPreview?.image ? (
          <button
            onClick={() => setShowImageLightbox(true)}
            className="w-full h-full group cursor-zoom-in"
          >
            <img
              src={event.image || event.linkPreview?.image || ''}
              alt={event.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
          </button>
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>

      <div className="space-y-6 md:space-y-8">
        {/* Title and Category */}
        <div>
          <div className="flex items-start justify-between mb-4">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 flex-1">{event.title}</h1>

            <div className="flex items-center gap-2 ml-4">
              {/* Edit and Delete Buttons - Only for event owner */}
              {user && user.uid === event.createdBy && (
                <>
                  <button
                    onClick={() => navigate(`/events/${event.id}/edit`)}
                    className="flex items-center gap-2 px-3 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 border border-primary-300 hover:border-primary-400 rounded-lg transition-colors"
                    title="Edit this event"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-sm font-medium">Edit</span>
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-300 hover:border-red-400 rounded-lg transition-colors"
                    title="Delete this event"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="text-sm font-medium">Delete</span>
                  </button>
                </>
              )}

              {/* Report Button */}
              {user && user.uid !== event.createdBy && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-300 hover:border-red-400 rounded-lg transition-colors"
                  title="Report this event"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-sm font-medium">Report</span>
                </button>
              )}
            </div>
          </div>
          
          {eventCategory && (
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: eventCategory.color || '#3b82f6' }}
            >
              {eventCategory.icon && <span>{eventCategory.icon}</span>}
              <span>{eventCategory.name}</span>
            </div>
          )}
        </div>

        {/* Date, Location, and Price */}
        <div className="bg-gray-50 rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="flex flex-col">
                <span className="text-gray-700">
                  {event.startDate && event.endDate && event.startDate !== event.endDate
                    ? `${new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(event.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : event.startDate
                      ? new Date(event.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                      : event.date || 'Date not specified'
                  }
                </span>
                {event.startTime && event.endTime && (
                  <span className="text-sm text-gray-600 mt-1">
                    {event.startTime} - {event.endTime}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-gray-700">
                {event.location?.name || 'Location not specified'}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <div>
              {event.isFree ? (
                <span className="inline-block bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-medium">
                  FREE
                </span>
              ) : (
                <span className="inline-block bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium">
                  ${event.cost}
                </span>
              )}
            </div>

            {/* Interested Button */}
            {user && user.uid !== event.createdBy && (
              <button
                onClick={handleToggleInterested}
                disabled={interestedLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isInterested
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-white text-primary-600 border-2 border-primary-600 hover:bg-primary-50'
                }`}
              >
                {interestedLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill={isInterested ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{isInterested ? 'Interested' : 'I\'m Interested'}</span>
                    {event.interested && event.interested > 0 && (
                      <span className={`text-sm ${isInterested ? 'text-primary-100' : 'text-primary-700'}`}>
                        ({event.interested})
                      </span>
                    )}
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Creator */}
        {event.createdByName && (
          <button
            onClick={handleCreatorClick}
            className="w-full bg-gray-50 rounded-2xl p-4 flex items-center gap-4 hover:bg-gray-100 transition-colors text-left"
          >
            {event.createdByAvatar ? (
              <img
                src={event.createdByAvatar}
                alt={event.createdByName}
                className="w-12 h-12 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm text-gray-500">Created by</p>
              <p className="font-semibold text-gray-900">{event.createdByName}</p>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Website */}
        {event.website && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Website</h3>
            {event.linkPreview ? (
              <button
                onClick={handleWebsiteClick}
                className="w-full bg-gray-50 rounded-2xl overflow-hidden hover:bg-gray-100 transition-colors text-left"
              >
                <div className="p-4 flex gap-4 items-center">
                  {/* Preview Image - Small rectangle on the side */}
                  {event.linkPreview.image && (
                    <div className="w-32 h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={event.linkPreview.image}
                        alt="Website preview"
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Text Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 items-start">
                      {event.linkPreview.favicon && (
                        <img
                          src={event.linkPreview.favicon}
                          alt="Favicon"
                          className="w-4 h-4 rounded flex-shrink-0 mt-0.5"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {event.linkPreview.title || event.website}
                        </p>
                        {event.linkPreview.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                            {event.linkPreview.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 truncate mt-1">{event.website}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            ) : (
              <button
                onClick={handleWebsiteClick}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="underline">{event.website}</span>
              </button>
            )}
          </div>
        )}

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {event.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Location Map */}
        {event.location?.coordinates && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Location</h3>
            <div className="bg-white rounded-2xl p-3 sm:p-4 border border-gray-200">
              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-900">{event.location.name}</h4>
                <p className="text-sm text-gray-600">{event.location.address}</p>
              </div>
              <EventMap 
                location={event.location} 
                className="h-64 sm:h-80 w-full"
              />
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {event.description}
            </p>
          </div>
        </div>

        {/* Comments Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Discussion & Questions</h3>
          <CommentSection
            targetType="event"
            targetId={event.id!}
            eventId={event.id!}
            eventTitle={event.title}
            showCommentCount={false}
            collapsible={false}
          />
        </div>

        {/* Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-8">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.082 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">Disclaimer</h4>
              <p className="text-sm text-yellow-800 leading-relaxed">
                The Daily Do is a community platform for discovering and sharing events. We do not host, sponsor, or endorse any events listed on the site (unless explicitly noted). Event information is submitted by users or organizers, and while we aim to provide accurate listings, we cannot guarantee details such as safety, quality, or reliability. Attendance is at your own discretion and risk.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {event && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          targetType="event"
          targetId={event.id || ''}
          targetTitle={event.title}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.082 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete Event</h3>
                <p className="text-gray-600 text-sm">
                  Are you sure you want to delete "{event?.title}"? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEvent}
                disabled={deleting}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  'Delete Event'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Sign In Required</h3>
                <p className="text-gray-600 text-sm">
                  You need to sign in or create an account to view event details and connect with your community.
                </p>
              </div>
            </div>

            <div className="space-y-3 mt-6">
              <button
                onClick={handleSignIn}
                className="w-full px-4 py-3 text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Sign In
              </button>
              <button
                onClick={handleSignUp}
                className="w-full px-4 py-3 text-primary-600 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Create Account
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-4 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-xl transition-colors font-medium"
              >
                Back to Events
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox Modal */}
      {showImageLightbox && (event?.image || event?.linkPreview?.image) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 cursor-zoom-out"
          onClick={() => setShowImageLightbox(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setShowImageLightbox(false)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Full size image */}
          <img
            src={event.image || event.linkPreview?.image || ''}
            alt={event.title}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            referrerPolicy="no-referrer"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Image title */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg text-sm max-w-md text-center">
            {event.title}
          </div>
        </div>
      )}
    </div>
  );
}